import { useContext, useEffect, useState } from 'react';
import { FollowUpCardTaxesController } from 'fragments/follow-up-card/follow-up-card-taxes/interfaces';
import { FollowUpContext } from 'fragments/follow-up-card/follow-up.context';
import { Form } from 'antd';
import { LiabilitiesService, useAPILiabilitiesService } from 'services/liabilities/liabilities.service';
import { mapLiabilityToEditFormInputs } from 'services/liabilities/liabilities.mapper';
import { EditLiabilityFormInputs } from 'fragments/property-card/create-edit-liability/interfaces';
import { useMessenger, UIMessenger } from 'tools/view-hooks/messenger-hook';

export const useFollowUpCardTaxesController = (
    liabilitiesService: LiabilitiesService = useAPILiabilitiesService(),
    messenger: UIMessenger = useMessenger(),
): FollowUpCardTaxesController => {
    /* State */
    const { followUp, setFollowUp } = useContext(FollowUpContext);
    const [isAddServiceVisible, setIsAddServiceVisible] = useState(false);
    const [isEditServiceVisible, setIsEditServiceVisible] = useState(false);
    const [isEditFormVisible, setIsEditFormVisible] = useState(false);
    const [editFormInitialValues, setEditFormInitialValues] = useState<EditLiabilityFormInputs | undefined>(undefined);
    const [form] = Form.useForm();
    const [editForm] = Form.useForm();

    useEffect(() => {
        if (!followUp.liabilities) {
            fetchLiabilities();
        }
    }, [followUp]);

    const onClickOkayInAddServiceModal = async () => {
        form.submit();
    };

    const onLiabilityAdded = () => {
        fetchLiabilities();
        setIsAddServiceVisible(false);
    };

    const onClickCancelInAddServiceModal = () => {
        setIsAddServiceVisible(false);
    };

    const onClickAddService = () => {
        setIsAddServiceVisible(true);
    };

    const onClickEditService = () => {
        const liabilities = followUp.liabilities ?? [];
        if (liabilities.length === 0) {
            return;
        }
        if (liabilities.length === 1) {
            onSelectLiabilityToEdit(liabilities[0].id);
            return;
        }
        setIsEditServiceVisible(true);
    };

    const onClickCancelInEditServiceModal = () => {
        setIsEditServiceVisible(false);
        setIsEditFormVisible(false);
        setEditFormInitialValues(undefined);
    };

    const onSelectLiabilityToEdit = (id: number) => {
        liabilitiesService
            .getLiability(id)
            .then((liability) => {
                const initialValues = mapLiabilityToEditFormInputs(liability);
                if (!initialValues) return;
                setEditFormInitialValues(initialValues);
                setIsEditServiceVisible(false);
                setIsEditFormVisible(true);
            })
            .catch(() => {
                // Show error ?
            });
    };

    const onClickOkayInEditServiceModal = () => {
        editForm.submit();
    };

    const onLiabilityEdited = () => {
        fetchLiabilities();
        setIsEditFormVisible(false);
        setEditFormInitialValues(undefined);
    };

    // Only ever removes the one liability the user confirmed — the rest of followUp.liabilities is
    // spread through untouched, and the payment sub-fragment reads from that same followUp state, so
    // it drops out of the card immediately too, not just this modal's list.
    const onClickDeleteLiability = (id: number) => {
        liabilitiesService
            .deleteLiability(id)
            .then((successful) => {
                if (!successful) {
                    messenger.showErrorMessage({ key: 'followUp.delete-liability-error' });
                    return;
                }
                setFollowUp((prevState) => ({
                    ...prevState,
                    liabilities: prevState.liabilities?.filter((liability) => liability.id !== id),
                }));
            })
            .catch(() => {
                messenger.showErrorMessage({ key: 'followUp.delete-liability-error' });
            });
    };

    /* Private Methods */

    const fetchLiabilities = () => {
        if (!followUp.id) {
            return;
        }
        liabilitiesService
            .getAllFollowUpLiabilities(followUp.id)
            .then((liabilitiesDtos) => {
                setFollowUp((prevState) => {
                    return { ...prevState, liabilities: liabilitiesDtos };
                });
            })
            .catch(() => {
                // Show error ?
            });
    };

    // Return state and events
    return {
        form,
        editForm,
        followUp,
        isAddServiceVisible,
        isEditServiceVisible,
        isEditFormVisible,
        editFormInitialValues,
        onClickCancelInAddServiceModal,
        onClickAddService,
        onClickOkayInAddServiceModal,
        onLiabilityAdded,
        onClickEditService,
        onClickCancelInEditServiceModal,
        onSelectLiabilityToEdit,
        onClickOkayInEditServiceModal,
        onLiabilityEdited,
        onClickDeleteLiability,
    };
};
